import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownardsAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Newtownards | Commuter Stress Hypnotherapy Ards</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Newtownards from Belfast clinic (12 miles, 20-25 min). Commuter stress, work-life balance, anxiety treatment. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Newtownards, stress management Newtownards, commuter stress Ards, hypnotherapy Newtownards County Down, work-life balance anxiety, therapist Newtownards" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownards-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Newtownards | Commuter Stress Hypnotherapy Ards" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Newtownards from Belfast. Ultra-convenient 12 miles. Commuter stress, work-life balance, anxiety treatment." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety and Stress Treatment Newtownards",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Newtownards from Belfast clinic. Specializing in commuter stress, work-life balance anxiety, anxiety disorders, panic attacks, and supporting North Down professionals and families.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownards-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Newtownards",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Newtownards from your Belfast clinic for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Newtownards is just 12 miles from the Belfast clinic via the A20, taking only 20-25 minutes by car. This ultra-convenient distance makes regular anxiety treatment appointments incredibly practical - closer than many Belfast suburbs. Many Newtownards commuters already make this journey daily for work, so adding therapy to your Belfast routine is straightforward."
                }
              },
              {
                "@type": "Question",
                "name": "Can I fit anxiety treatment around my work schedule?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer flexible appointment times including early morning, daytime, and evening slots to accommodate work schedules. At just 12 miles from Belfast, many Newtownards commuters add their therapy session before or after work, or during lunch breaks. Online sessions are also available if you prefer the convenience of attending from home, saving travel time entirely."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for commuter stress and work anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for commuter-related stress and work anxiety. Many Newtownards residents commute to Belfast for work, dealing with time poverty, work-life balance challenges, and the exhaustion of living in two locations. Hypnotherapy helps by calming the stress response, building resilience, improving sleep quality, and developing healthier boundaries around work pressures."
                }
              },
              {
                "@type": "Question",
                "name": "How long does anxiety treatment take?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients notice significant improvements within 4-6 sessions for anxiety and stress. Some people feel calmer after just one or two sessions. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with work-life balance anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many Newtownards residents struggle with the demands of Belfast employment, long commutes, family responsibilities, and finding time for themselves. Solution-focused hypnotherapy helps you manage stress, set boundaries, build resilience, and create a healthier relationship with work. You learn to switch off, prioritize, and protect your wellbeing."
                }
              },
              {
                "@type": "Question",
                "name": "Is evening availability offered for busy professionals?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I offer evening appointments to accommodate busy professionals and those with work commitments during standard hours. At just 12 miles from Belfast, many Newtownards clients find it easy to add an evening therapy session after work. Online sessions are also available if you prefer to attend from home, which can be even more convenient for fitting therapy around your schedule."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Newtownards
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for North Down commuters and families - ultra-convenient from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Just 12 miles</span> from Newtownards via A20 • <span className="font-semibold">20-25 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Ultra-convenient for regular sessions • Online also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Newtownards */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Newtownards
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Newtownards and struggling with anxiety or stress, you're not alone. With a population of around 28,000, Newtownards functions as a commuter town for Belfast professionals, gateway to the Ards Peninsula, and home to busy families balancing work, school runs, and the demands of suburban life. This Belfast-adjacent location creates unique pressures that can significantly impact mental health and wellbeing.
              </p>
              <p>
                Many Newtownards residents experience anxiety related to commuter stress and work-life balance challenges. The daily commute to Belfast for work - whether it's 20 minutes on a good day or 45 minutes in traffic - creates time poverty, exhaustion, and the sense of living in two places at once. You're constantly rushing: leaving early for work, racing back for school pickups, missing family dinners, feeling guilty about not being present. The anxiety builds as you try to be a committed employee, a present parent, a supportive partner, and somehow find time for yourself. It's exhausting.
              </p>
              <p>
                For many dual-income families in Newtownards, both parents commute to Belfast, creating complex logistics around childcare, school runs, and who can leave work early if a child is sick. The retail and service sector employment at Ards Shopping Centre adds additional pressures - customer-facing stress, weekend work, and the emotional labor of staying polite when you're exhausted. Add financial worries, family responsibilities, and the physical symptoms of chronic anxiety - tight chest, racing heart, poor sleep, constant tension - and it's no wonder stress can feel overwhelming.
              </p>
              <p>
                The good news is that Newtownards is incredibly well-placed for accessing specialist anxiety treatment. My Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast is just 12 miles from Newtownards via the A20 - that's only 20-25 minutes in typical traffic. This ultra-convenient distance means regular anxiety treatment appointments are entirely practical. Many Newtownards clients already make this journey daily for work, so adding therapy to your Belfast routine is straightforward. For those who prefer the convenience of staying local, or want to avoid travel time entirely, I also offer online sessions that provide the same professional treatment from the comfort of your home.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Newtownards Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Difficulty falling or staying asleep',
                    'Racing thoughts, especially at night',
                    'Physical symptoms: tight chest, pounding heart, tense shoulders',
                    'Constant sense of unease or dread',
                    'Avoiding situations out of fear or worry',
                    'Feeling overwhelmed by everyday tasks',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Newtownards-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Belfast commuter stress and time poverty',
                    'Work-life balance with dual-income demands',
                    'Complex school run logistics and childcare',
                    'Guilt about missing family time due to work',
                    'Exhaustion from living in two locations',
                    'Retail sector stress at Ards Shopping Centre',
                    'Parenting anxiety for busy families',
                    'Financial pressure from dual-location living'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                When anxiety and stress dominate your life, it feels like you're constantly running but never catching up. The racing thoughts, the physical tension, the exhaustion, the guilt - it affects your sleep, your relationships, your ability to be present with your family, and your capacity to enjoy life. But you don't have to live like this. There is real, effective help available. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you regain control, set boundaries, and build lasting calm.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety: What's Really Happening
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Anxiety is incredibly common - around 1 in 4 people in the UK will experience an anxiety disorder at some point in their lives. But knowing it's common doesn't make it any easier to live with. Anxiety is your brain's natural alarm system going into overdrive, interpreting everyday situations - work deadlines, traffic jams, school pickups, social events - as serious threats and triggering your fight-or-flight response even when there's no real danger present.
              </p>
              <p>
                When you're anxious, your brain releases stress hormones like cortisol and adrenaline, preparing your body to respond to a perceived threat. This is why you experience physical symptoms - racing heart, tight chest, shallow breathing, muscle tension, digestive issues, feeling shaky or on edge. Your brain is literally preparing you to fight or run away, even when you're just sitting in traffic on the way to work or lying in bed trying to sleep.
              </p>
              <p>
                Anxiety disorders include generalized anxiety disorder (persistent, excessive worry about work, family, health, finances), social anxiety (fear of social situations and judgment), panic disorder (sudden, intense panic attacks), and specific phobias. Many people experience a combination of symptoms that don't fit neatly into one category. What matters is not the diagnostic label, but the impact anxiety is having on your quality of life, your relationships, and your ability to function day-to-day.
              </p>
              <p>
                The good news is that anxiety is highly treatable. Your brain created these anxious patterns through repeated stress and worry, and your brain can learn new, healthier patterns. Solution-focused hypnotherapy works by calming the overactive stress response, helping you understand your triggers, building resilience, and creating lasting change in how you think about and respond to stressful situations. You can learn to set boundaries, switch off, and protect your wellbeing without sacrificing your responsibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from worry and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so panic and tension reduce, and clarity returns. It's not about dwelling on the past or analyzing every source of stress - it's about building the resilience, boundaries, and calm you need to move forward with confidence.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms like racing heart, tight chest, and tense muscles. Your body learns to relax again, and the constant state of high alert fades away.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Rewire Thought Patterns',
                  text: 'Build new habits of thought and behavior that support resilience and wellbeing. Replace anxious, overwhelmed thinking with calmer, more balanced responses to work pressures, family demands, and daily challenges.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Create a calmer pre-sleep routine and quiet the racing mind that keeps you awake despite exhaustion. Wake feeling more refreshed and better equipped to handle the demands of commuting, work, and family life.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-white rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl text-center">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety disorder',
                    'Social anxiety and performance pressure',
                    'Panic attacks and panic disorder',
                    'Work-related stress and burnout',
                    'Commuter stress and time poverty'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Work-life balance anxiety and guilt',
                    'Parenting stress for dual-income families',
                    'Sleep problems from racing thoughts',
                    'Financial anxiety and pressure',
                    'Relationship tension from stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Sharon's Approach to Anxiety Treatment
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. My background in nursing means I understand the medical and neurological side of anxiety - the brain chemistry, the physical symptoms, the interactions with other health conditions. But more importantly, I understand the human side: the exhaustion, the guilt, the desperate need for relief, and the courage it takes to admit you need help.
              </p>
              <p>
                I use solution-focused hypnotherapy, an evidence-based approach that combines talking therapy with hypnosis. Each session begins with structured conversation about your week, focusing on what's working, what you want to achieve, and practical steps forward. This isn't about dwelling on problems or analyzing every source of stress - it's about building momentum toward positive change and identifying what you can control. Then we move into hypnosis, a deeply relaxed state where your subconscious mind becomes more receptive to positive suggestions and new patterns of thinking.
              </p>
              <p>
                Hypnosis isn't sleep, and it's not stage hypnosis. You remain completely aware and in control throughout. It's simply a state of focused attention and deep relaxation - similar to being absorbed in a good book or daydreaming on a familiar journey. In this relaxed state, we work with your subconscious mind to calm the overactive anxiety response, build resilience, set healthier boundaries, and create lasting change in how you think about and respond to stressful situations.
              </p>
              <p>
                Most clients notice significant improvements within 4-6 sessions, though everyone's journey is different. Some people feel noticeably calmer and more in control after just one or two sessions. Others need a bit more time to build lasting change, especially if anxiety has been present for years. What matters is that you're moving forward at your own pace, supported by evidence-based treatment that addresses both the symptoms and the underlying causes of anxiety. You don't have to live with constant worry, guilt, and exhaustion. Help is available, and lasting change is absolutely possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newtownards
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Ultra-Convenient)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the A20 westbound into Belfast, then head south out along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Just 12 miles - closer than many Belfast suburbs
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 20-25 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Ultra-Convenient for Regular Treatment</h3>
                <p className="text-gray-700">
                  At just 12 miles via the A20, the journey from Newtownards to Belfast is incredibly practical for regular anxiety treatment appointments. Many Newtownards residents already make this commute daily for work, so adding therapy to your Belfast routine is straightforward. The short 20-25 minute journey can become therapeutic time - dedicated space away from work pressures and family responsibilities to focus on yourself and your wellbeing. Some clients schedule sessions before or after work, others during lunch breaks, or on non-work days. The ultra-convenient distance makes it easy to fit therapy into your busy schedule.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Want to avoid travel time entirely? Prefer the comfort of home? I offer secure online hypnotherapy sessions via video call. Online sessions provide exactly the same professional, evidence-based anxiety treatment with the convenience of attending from your home in Newtownards. This option is ideal for those with mobility concerns, caring responsibilities, irregular work hours, or anyone who wants maximum flexibility. Save the 40-50 minutes of travel time and use it for self-care instead. The treatment is just as effective - only the location changes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newtownards Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Between commuting to Belfast every day for work, the school runs, and trying to be present for my kids, I was completely burnt out. Constant anxiety, terrible sleep, snapping at everyone. Sharon's hypnotherapy helped me set boundaries, manage the stress, and actually switch off when I'm home. I'm still doing the same commute and the same job, but I handle it so much better now. I can be present with my family instead of constantly worrying. The anxiety is still there sometimes, but it doesn't control me anymore."
                </p>
                <p className="font-semibold text-gray-900">K.L., County Down - Commuter Stress and Work-Life Balance</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The panic attacks were terrifying - racing heart, couldn't breathe, convinced I was dying. I was avoiding driving to Belfast for work, making excuses, living in constant fear. Sharon helped me understand what was happening in my brain and gave me tools to manage it. After six sessions, the panic attacks have stopped completely. I can commute without fear, I'm sleeping properly again, and I feel like I've got my life back. I started with online sessions because I was too anxious to drive, and they worked brilliantly."
                </p>
                <p className="font-semibold text-gray-900">S.M., North Down - Panic Attacks and Driving Anxiety</p>
              </div>

              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working retail with the long hours, difficult customers, and weekend shifts, plus trying to coordinate everything with my partner's work schedule - I was exhausted and anxious all the time. Couldn't relax, couldn't switch off, constant tension. Sharon's approach helped me manage the work stress and build resilience. I'm still in retail, but I cope completely differently now. The anxiety doesn't overwhelm me like it used to. Being able to book sessions around my shifts made it so easy to commit to the process."
                </p>
                <p className="font-semibold text-gray-900">R.T., County Down - Retail Worker Anxiety and Burnout</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownards Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Newtownards from your Belfast clinic for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Newtownards is just 12 miles from the Belfast clinic via the A20, taking only 20-25 minutes by car. This ultra-convenient distance makes regular anxiety treatment appointments incredibly practical - closer than many Belfast suburbs. Many Newtownards commuters already make this journey daily for work, so adding therapy to your Belfast routine is straightforward.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I fit anxiety treatment around my work schedule?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer flexible appointment times including early morning, daytime, and evening slots to accommodate work schedules. At just 12 miles from Belfast, many Newtownards commuters add their therapy session before or after work, or during lunch breaks. Online sessions are also available if you prefer the convenience of attending from home, saving travel time entirely.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for commuter stress and work anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for commuter-related stress and work anxiety. Many Newtownards residents commute to Belfast for work, dealing with time poverty, work-life balance challenges, and the exhaustion of living in two locations. Hypnotherapy helps by calming the stress response, building resilience, improving sleep quality, and developing healthier boundaries around work pressures.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How long does anxiety treatment take?
                </h3>
                <p className="text-gray-700">
                  Most clients notice significant improvements within 4-6 sessions for anxiety and stress. Some people feel calmer after just one or two sessions. The number varies based on your individual circumstances, the severity of symptoms, and your goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with work-life balance anxiety?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for anxiety related to work-life balance challenges. Many Newtownards residents struggle with the demands of Belfast employment, long commutes, family responsibilities, and finding time for themselves. Solution-focused hypnotherapy helps you manage stress, set boundaries, build resilience, and create a healthier relationship with work. You learn to switch off, prioritize, and protect your wellbeing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is evening availability offered for busy professionals?
                </h3>
                <p className="text-gray-700">
                  Yes, I offer evening appointments to accommodate busy professionals and those with work commitments during standard hours. At just 12 miles from Belfast, many Newtownards clients find it easy to add an evening therapy session after work. Online sessions are also available if you prefer to attend from home, which can be even more convenient for fitting therapy around your schedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Calm"
        subtitle="Take the first step toward less anxiety, better work-life balance, and more peace. Book a free consultation today and discover how hypnotherapy can help you regain control and find calm."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default NewtownardsAnxietyStress;
